import type React from 'react'
import { useCallback, useState } from 'react'
import { AiOutlineCheck, AiOutlineStop } from 'react-icons/ai'
import { BsExclamationTriangle } from 'react-icons/bs'
import { BiAlarmExclamation } from 'react-icons/bi'
import { PayButton, ProgressBar, Status, Wrapper } from './styles'
import { ModalPix } from 'components/PaymentModalPix'
import { ModalCreditCard } from 'components/PaymentCreditCard'
import { useGetInvoiceHistory } from 'pages/Profile/hooks/use-get-invoice-history'
import { learningApi } from 'services/api'
import { toast } from 'react-toastify'

const PaymentForm: React.FC<{ order?: string }> = ({ order }) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [modalPixOpen, setModalPixOpen] = useState(false)
  const [modalCreditCardOpen, setModalCreditCardOpen] = useState(
    order && !!new URLSearchParams(location.search).get('edit')
  )
  const [selectedCharge, setSelectedCharge] = useState({})
  const [pixNumber, setPixNumber] = useState('')

  const token = localStorage.getItem('token')

  const swrInvoiceHistory = useGetInvoiceHistory({ order })
  const paymentData = swrInvoiceHistory.isLoading ? [] : swrInvoiceHistory.data

  const getSituation = useCallback((situation: string) => {
    if (situation === 'grant' || situation === 'authorized' || situation === 'paid') {
      return (
        <Status status="pay">
          <AiOutlineCheck /> Liberado
        </Status>
      )
    }
    if (
      situation === 'processing' ||
      situation === 'waiting_payment' ||
      situation === 'analyzing' ||
      situation === 'pending' ||
      situation === 'pending_review'
    ) {
      return (
        <Status status="released">
          <BiAlarmExclamation /> Pendente
        </Status>
      )
    }
    if (
      situation === 'refunded' ||
      situation === 'pending_refund' ||
      situation === 'chargedback' ||
      situation === 'canceled'
    ) {
      return (
        <Status status="refused">
          <AiOutlineStop /> Cancelado
        </Status>
      )
    }
    if (situation === 'refused' || situation === 'failed' || situation === 'abandoned') {
      return (
        <Status status="refused">
          <BsExclamationTriangle size={14} /> Não autorizado
        </Status>
      )
    }
  }, [])

  const getProductsName = useCallback((products: any) => {
    const productsName: any = []
    for (const item of products) {
      if (item.completePackage) return productsName.push(item.name)

      if (item.type === 'subscription') return productsName.push(item.name)

      for (const itm of item.items) {
        productsName.push(itm.name)
      }
    }

    return productsName.join(', ')
  }, [])

  const handleUpdateCreditCard = useCallback((charge) => {
    setSelectedCharge(charge)
    setModalCreditCardOpen(true)
  }, [])

  const handleRetry = useCallback(
    async (charge: any) => {
      setLoading(true)

      try {
        await learningApi.post(
          `/order/retry/${order}/${charge?._id}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        toast.success('Dados alterados com sucesso')

        setLoading(false)
      } catch (error) {
        console.log(error)
        return toast.error('Algo deu errado, tente novamente mais tarde')
      } finally {
        setLoading(false)
      }
    },
    [order, token]
  )

  const charges = order && paymentData.length && paymentData.find((p) => p._id === order)?.charges

  return (
    <>
      {modalPixOpen && <ModalPix pixNumber={pixNumber} setModalOpen={setModalPixOpen} />}
      {modalCreditCardOpen && (
        <ModalCreditCard
          charge={selectedCharge}
          setModalOpen={setModalCreditCardOpen}
          order={order}
        />
      )}
      <Wrapper>
        <h2>Histórico de faturas</h2>

        <table>
          <tr>
            <th>Pedido</th>
            <th className="onlyDesktop">Data da cobrança</th>
            <th className="onlyDesktop">Produto</th>
            <th className="onlyDesktop">Status</th>
            <th>Valor</th>
            <th>Pagamento</th>
            <th>Descrição</th>
          </tr>

          {paymentData.length !== 0 ? (
            paymentData.map((item) => {
              return (
                <tr key={item?._id}>
                  <td>{item.number}</td>
                  <td>{new Date(item.createdAt).toLocaleString('pt-BR')}</td>
                  <td title={getProductsName(item.products)}>{getProductsName(item.products)}</td>
                  <td>{getSituation(item.situation)} </td>
                  <td>
                    {(item.sale.net >= 0 ? item.sale.net : 0).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </td>

                  <td className="onlyDesktop">
                    {item.payment?.method === 'pix' ? (
                      <PayButton
                        onClick={() => {
                          setModalPixOpen(true)
                          setPixNumber(item.payment?.qrcode)
                        }}
                        disabled={
                          item.situation === 'grant'
                            ? true
                            : item.situation === 'authorized'
                            ? true
                            : item.situation === 'paid'
                        }
                      >
                        Copiar pix
                      </PayButton>
                    ) : item.payment?.method === 'billet' ? (
                      <a href={item.payment?.url || '#'} target="_blank" rel="noreferrer">
                        <PayButton
                          disabled={
                            item.situation === 'grant'
                              ? true
                              : item.situation === 'authorized'
                              ? true
                              : item.situation === 'paid'
                          }
                        >
                          Baixar boleto
                        </PayButton>
                      </a>
                    ) : (
                      'Cartão de crédito'
                    )}
                  </td>
                  <td>{item?.payment?.acquirer?.message}</td>
                </tr>
              )
            })
          ) : (
            <h3 className="textNoHave">Não há faturas no histórico.</h3>
          )}
        </table>
        {order ? (
          <div style={{ marginTop: '30px' }}>
            <h2>Cobranças</h2>
            {charges && charges.length > 0 ? (
              <>
                <table>
                  <tr>
                    <th>Período</th>
                    <th>Cobrança</th>
                    <th>Cartão de crédito</th>
                    <th>Valor</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                  {charges.map((charge) => {
                    return (
                      <tr key={charge?._id}>
                        <td>{charge.period}</td>
                        <td>{charge.chargedAt}</td>
                        <td>
                          {charge?.transaction?.card?.brand} -{' '}
                          {charge?.transaction?.card?.lastFourDigits}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {charge?.type === 'standard' ? (
                            <>
                              {charge.installments}
                              {(
                                charge?.transaction?.amount / charge?.transaction?.installments
                              ).toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              })}
                            </>
                          ) : (
                            <>
                              {charge?.transaction?.amount.toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              })}
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  fontSize: 10,
                                  gap: 5,
                                }}
                              >
                                {charge?.installments}
                                <ProgressBar percentage={charge.percent} />
                              </div>
                            </>
                          )}
                        </td>
                        <td>{getSituation(charge.status)}</td>
                        <td>
                          {charge.last && ['pending', 'processing'].includes(charge?.status) ? (
                            <PayButton onClick={() => handleRetry(charge)}>Reprocessar</PayButton>
                          ) : charge.last ? (
                            <PayButton
                              disabled={loading}
                              onClick={() =>
                                handleUpdateCreditCard(
                                  ['failed', 'refused'].includes(charge?.status) ? charge : null
                                )
                              }
                            >
                              Atualizar cartão
                            </PayButton>
                          ) : (
                            <></>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </table>
              </>
            ) : (
              <></>
            )}
          </div>
        ) : (
          <></>
        )}
      </Wrapper>
    </>
  )
}

export default PaymentForm
